<template>
  <Button
    :id="id"
    :disabled="disabled || readonly || loading"
    :aria-label="ariaLabel ?? label"
    :class="[
      'group w-full max-w-[200px] flex flex-col items-center justify-start gap-0 rounded-2xl border-none bg-white p-0',
      'hover:shadow-sm focus:border-none focus:outline-hidden focus:ring-2 focus:ring-offset-1 focus:ring-[#3F3F3F]',
      'disabled:opacity-50 disabled:pointer-events-none',
      pt?.root
    ]"
    v-bind="filteredAttrs"
    @click="onClick"
    @focus="$emit('focus', $event)"
    @blur="$emit('blur', $event)"
  >
    <div :class="['w-full flex flex-col items-center', pt?.body]">
      <!-- Верхняя картинка не меняет цвет на ховере -->
      <slot name="image">
        <div class="relative w-full h-[168px]">
          <img
            v-if="imageSrc"
            :src="imageSrc"
            :alt="imageAlt || ''"
            :class="[
              'w-full h-full object-cover rounded-t-2xl select-none transition-opacity duration-200 group-hover:opacity-60',
              pt?.image
            ]"
            draggable="false"
          />

          <!-- Оверлей (показывается на hover) -->
          <div
            v-if="textHover"
            :class="[
              'absolute inset-0 rounded-t-2xl  flex items-center px-4 text-center',
              'bg-[#FBFBFBCC] text-black opacity-0 transition-opacity duration-200',
              'group-hover:opacity-80 pointer-events-none',
              pt?.overlay
            ]"
            aria-hidden="true"
          >
            <span class="text-[13px] text-left overflow-auto">
              {{ textHover }}
            </span>
          </div>
        </div>
      </slot>

      <!-- Низ: иконка + текст с ховером -->
      <div
        :class="[
          'w-full h-[48px] flex justify-center items-center gap-2 px-4',
          'bg-white rounded-b-2xl transition-colors',
          'group-hover:bg-[#E2FF32]',
          pt?.footer
        ]"
      >
        <slot name="icon">
          <img
            v-if="iconSrc"
            :src="iconSrc"
            alt=""
            :class="['w-5 h-5 shrink-0 select-none', pt?.icon]"
            draggable="false"
          />
        </slot>

        <slot name="label">
          <span :class="['text-sm font-medium text-[#151515] truncate', pt?.label]">
            {{ label }}
          </span>
        </slot>
      </div>
    </div>
  </Button>
</template>

<script setup lang="ts">
import Button from 'primevue/button';
import { computed, useAttrs } from 'vue';
import { useRouter } from 'vue-router';

type PtClasses = {
  root?: string;
  body?: string;
  image?: string;
  footer?: string;
  icon?: string;
  label?: string;
  overlay?: string;
};

const props = defineProps<{
  id?: string;
  imageSrc?: string;
  imageAlt?: string;
  iconSrc?: string;
  label: string;
  disabled?: boolean;
  readonly?: boolean;
  loading?: boolean;
  ariaLabel?: string;
  pt?: PtClasses;
  to?: string;
  textHover?: string;
  replace?: boolean;
  href?: string;
  target?: '_blank' | '_self' | '_parent' | '_top';
}>();

const emit = defineEmits<{
  (e: 'click', ev: MouseEvent): void;
  (e: 'focus', ev: FocusEvent): void;
  (e: 'blur', ev: FocusEvent): void;
}>();

const router = useRouter();
const attrs = useAttrs();
const filteredAttrs = computed(() => {
  const { class: _c, style: _s, ...rest } = attrs as Record<string, unknown>;
  return rest;
});

async function onClick(ev: MouseEvent) {
  if (props.readonly || props.loading || props.disabled) {
    ev.preventDefault();
    ev.stopPropagation();
    return;
  }
  emit('click', ev);

  if (props.href) {
    window.open(props.href, props.target ?? '_blank', 'noopener,noreferrer');
    return;
  }

  if (typeof props.to === 'string' && /^https?:\/\//i.test(props.to)) {
    window.open(props.to, props.target ?? '_blank', 'noopener,noreferrer');
    return;
  }

  if (props.to) {
    try {
      if (props.replace) {
        await router.replace({ name: props.to });
      } else {
        await router.push({ name: props.to });
      }
    } catch {
      // no-op
    }
  }
}
</script>
